/**
 * pages/BlogPost.tsx
 * Route: /blog/:slug
 * Renders individual blog post. Content is static for now —
 * swap posts object for API call when CMS/backend is ready.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { PageNav, PageFooter, CookieBanner } from '../components/layout/LegalLayout';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── Post content ─────────────────────────────────────────────────────────────
const posts: Record<string, { tag:string; color:string; date:string; readTime:string; title:string; excerpt:string; body:string }> = {
  'why-search-first': {
    tag:'Product', color:'#F5A623', date:'March 18, 2026', readTime:'5 min read',
    title:'Why we built Jötr search-first (and why it changes everything)',
    excerpt:'Most note apps treat search as an afterthought. We built Jötr the other way around.',
    body:`
      <p>Most note-taking apps start with the editor. They obsess over fonts, formatting, and folder structures. Search gets bolted on at the end — a basic keyword match that only looks at note titles.</p>
      <p>We started with a different question: <strong>how do you find something you wrote six months ago at 11pm when you can only remember one word from it?</strong></p>
      <h2>The problem with most note apps</h2>
      <p>Evernote, Notion, Apple Notes — they all treat search the same way. You type a word, it checks if that word appears in the title or body, it returns results in chronological order. That's it.</p>
      <p>This works fine when you have 20 notes. When you have 2,000 notes spread across three years, it breaks down completely.</p>
      <h2>How Jötr search works</h2>
      <p>Jötr uses PostgreSQL full-text search with <code>tsvector</code> indexing. Every note is indexed the moment it is saved. Search results are ranked by relevance — title matches rank higher than body matches. Matched keywords are highlighted in context, so you can see exactly where in the note your search term appears.</p>
      <p>On average, Jötr returns results in under 200 milliseconds — even for users with thousands of notes.</p>
      <h2>Why this changes your workflow</h2>
      <p>When you trust search, you stop organising. You stop spending ten minutes deciding which folder a note belongs in. You write it, you tag it loosely, and you move on. You know you'll find it when you need it.</p>
      <p>This is how our best users work. They don't have elaborate folder hierarchies. They have one big inbox and they search for everything. Jötr makes that work.</p>
      <h2>What's next</h2>
      <p>We're building semantic search — the ability to find notes by meaning, not just keywords. Type "what did I write about the client meeting in October" and Jötr will understand what you mean. This is coming in V2.</p>
    `,
  },
  'note-taking-professionals-lagos': {
    tag:'Productivity', color:'#16A34A', date:'March 10, 2026', readTime:'8 min read',
    title:'How Lagos professionals are using digital notes to stay ahead',
    excerpt:'We spoke to 20 consultants, analysts, and MBA students in Lagos about their note-taking habits.',
    body:`
      <p>We spent three weeks talking to professionals in Lagos — consultants, financial analysts, MBA students, and entrepreneurs. We asked them one question: <strong>how do you capture and retrieve information in your work?</strong></p>
      <p>The answers surprised us. Here is what we learned.</p>
      <h2>Most professionals are still on paper</h2>
      <p>Of the 20 people we spoke to, 14 still keep physical notebooks as their primary note-taking system. Not as a supplement — as the primary system. WhatsApp saved messages came second. Digital note apps came last.</p>
      <h2>The problem isn't the app. It's trust.</h2>
      <p>The most common reason people gave for not switching to digital notes was simple: <em>"I don't trust I'll find it again."</em></p>
      <p>This makes complete sense. If search is bad, digital notes are worse than paper. At least with paper you remember roughly where something is.</p>
      <h2>What changes everything: instant, accurate search</h2>
      <p>When we showed participants Jötr's search — typing a partial word and getting highlighted results in under a second — the reaction was consistent. Everyone immediately started thinking of notes they'd lost.</p>
      <p>One participant, a financial analyst, typed "IRR" and found a calculation she'd written eight months earlier. She hadn't been able to find it in her physical notebook for weeks.</p>
      <h2>Implications for how we build Jötr</h2>
      <p>Search is not a feature. It is the product. Everything else — folders, tags, the editor — exists to support the moment when you need something and need it now.</p>
    `,
  },
  'tiptap-rich-text': {
    tag:'Engineering', color:'#7C3AED', date:'March 3, 2026', readTime:'12 min read',
    title:"Building a rich text editor with TipTap: lessons from Jötr's editor",
    excerpt:"Why we chose TipTap over Quill, Slate, and ProseMirror directly.",
    body:`
      <p>Choosing an editor library is one of the most consequential decisions in a note-taking app. Get it wrong and you're refactoring for months. Here is why we chose TipTap and what we learned building Jötr's editor.</p>
      <h2>The candidates</h2>
      <p>We evaluated four options seriously: Quill, Slate, Draft.js, and TipTap (built on ProseMirror). We also considered building directly on ProseMirror.</p>
      <p>Draft.js was eliminated first — Facebook deprecated it in 2022 and the community has largely moved on. Quill was next — it hasn't had a major release in years and the architecture makes custom extensions painful.</p>
      <h2>Why TipTap won</h2>
      <p>TipTap sits on top of ProseMirror — the most powerful document model in JavaScript — but exposes a React-friendly API. You get ProseMirror's power without writing ProseMirror's boilerplate.</p>
      <p>The extension system is excellent. We added <code>StarterKit</code>, <code>Placeholder</code>, and <code>Underline</code> with three import lines. Custom extensions follow a consistent pattern.</p>
      <h2>What we'd do differently</h2>
      <p>TipTap's collaboration extension (using Yjs) is more complex than the docs suggest. We underestimated the integration time. If you're building real-time collaboration from day one, budget two to three times longer than you expect.</p>
      <p>Also: test your editor on Android keyboards early. Mobile input events behave differently from desktop and several edge cases only appear on real devices.</p>
    `,
  },
  'zustand-vs-redux': {
    tag:'Engineering', color:'#2563EB', date:'February 24, 2026', readTime:'10 min read',
    title:'Zustand vs Redux in 2026: why we switched and what happened',
    excerpt:'We started with Redux Toolkit. After three months, we moved to Zustand.',
    body:`
      <p>We started Jötr with Redux Toolkit. It felt like the responsible choice — battle-tested, well-documented, strongly typed. Three months in, we switched to Zustand. Here is the honest account of why.</p>
      <h2>What RTK got right</h2>
      <p>Redux Toolkit eliminated the most painful Redux boilerplate. <code>createSlice</code> and <code>createAsyncThunk</code> made the pattern manageable. DevTools are excellent.</p>
      <h2>Where it broke down for us</h2>
      <p>Jötr's state is highly interconnected. Notes depend on folders. Search results reference notes. The editor has its own sync status. With RTK, every cross-slice operation required careful selector composition and often led to re-render cascades that were hard to trace.</p>
      <p>The boilerplate was still significant. A simple "pin a note" action required touching the slice, the selector, the component, and often a thunk. Four files for one interaction.</p>
      <h2>Zustand: what actually happened</h2>
      <p>The migration took two days. We rewrote the entire store in a single <code>index.ts</code> file. Actions, state, and selectors live together. The API is minimal.</p>
      <p>One critical lesson: <strong>never use inline object selectors with Zustand.</strong> <code>useStore(s => ({ a: s.a, b: s.b }))</code> creates a new object on every render and causes infinite loops. Use individual selectors or <code>useShallow</code>. We learned this the hard way.</p>
      <h2>The verdict</h2>
      <p>For a mid-sized React app with complex interdependent state, Zustand is significantly faster to work with than Redux Toolkit. The tradeoff is less structure — which is fine if your team is disciplined about the store shape.</p>
    `,
  },
  'free-forever-model': {
    tag:'Company', color:'#DB2777', date:'February 15, 2026', readTime:'4 min read',
    title:"Jötr is free forever — here's our business model",
    excerpt:"We're committed to a genuinely powerful free plan. Here is how we plan to make that sustainable.",
    body:`
      <p>We get asked this a lot: if the free plan is genuinely useful, how do you make money?</p>
      <p>It's a fair question. Too many "free" apps are actually crippled trials designed to frustrate you into paying. We don't want to build that.</p>
      <h2>What free means at Jötr</h2>
      <p>The free plan includes unlimited notes, full-text search, folders and tags, cloud sync across two devices, and the rich text editor. These are not limited versions of features. They are the full features.</p>
      <h2>How we make money</h2>
      <p>Jötr Pro adds features that power users need: unlimited devices, note sharing, image attachments with OCR search, version history, priority support, and an AI writing assistant (coming in V3).</p>
      <p>We target a 5-8% conversion rate from free to Pro. At that rate, a user base of 50,000 people generates enough revenue to run the company, pay a small team well, and keep investing in the product.</p>
      <h2>Why we believe in this model</h2>
      <p>The best software companies in the world — Notion, Linear, Figma — all grew through genuine free plans that users loved. When the product is good and the free plan is real, word of mouth does the marketing.</p>
      <p>We're playing a long game. We'd rather have a million users who love Jötr than 10,000 who feel trapped by it.</p>
    `,
  },
  'offline-first-pwa': {
    tag:'Engineering', color:'#C4811A', date:'February 8, 2026', readTime:'9 min read',
    title:'Building offline-first with service workers: our approach',
    excerpt:"In Nigeria, internet reliability is not a given. Here is how we built Jötr to work offline.",
    body:`
      <p>In Lagos, you can go from 4G to no signal in the time it takes to leave a building. If Jötr stopped working the moment connectivity dropped, it would be unusable for a large part of our user base.</p>
      <p>We built offline-first from the start. Here is how.</p>
      <h2>The service worker strategy</h2>
      <p>Jötr uses a service worker with two caching strategies: cache-first for the app shell (HTML, JS, CSS) and network-first for API calls.</p>
      <p>Cache-first means the app loads instantly on every visit, even with no network. The UI is always available.</p>
      <p>Network-first for API calls means you always get fresh data when online. When offline, the service worker returns the cached response — your notes from the last sync.</p>
      <h2>Local state with IndexedDB</h2>
      <p>When you create or edit a note offline, it is saved to IndexedDB immediately. A sync queue tracks unsaved changes. When connectivity returns, the queue flushes automatically and conflicts are resolved with a last-write-wins strategy.</p>
      <h2>What doesn't work offline</h2>
      <p>Search is limited offline — you can only search notes that have been previously loaded. Full search requires the PostgreSQL backend. We show a clear indicator when you're in offline mode.</p>
      <h2>Testing offline behaviour</h2>
      <p>Chrome DevTools Network tab has an offline mode that makes testing easy. We also use Playwright's network interception in CI to test offline scenarios automatically.</p>
    `,
  },
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? posts[slug] : null;

  if (!post) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
          *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
          body{font-family:'DM Sans',sans-serif;background:#FAFAF8;}
          #root{display:flex;flex-direction:column;min-height:100vh;}
        `}</style>
        <PageNav/>
        <main style={{ flex:1, maxWidth:700, margin:'0 auto', padding:'80px clamp(20px,5vw,40px)', textAlign:'center' }}>
          <p style={{ fontSize:48, marginBottom:20 }}>📝</p>
          <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:28, color:C.ink, marginBottom:12 }}>Post not found</h1>
          <p style={{ fontSize:15, color:C.muted, fontFamily:font.body, marginBottom:28 }}>This post doesn't exist or may have moved.</p>
          <a href="/blog" style={{ display:'inline-flex', padding:'11px 24px', borderRadius:10, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:font.body }}>
            Back to blog
          </a>
        </main>
        <PageFooter/>
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:#FAFAF8;color:#1A1814;}
        #root{display:flex;flex-direction:column;min-height:100vh;}

        .post-body h2 {
          font-family: 'Sora', sans-serif;
          font-weight: 700;
          font-size: clamp(18px, 2.5vw, 22px);
          color: #1A1814;
          margin: 36px 0 14px;
          letter-spacing: -0.02em;
          line-height: 1.3;
        }
        .post-body p {
          font-size: clamp(15px, 1.8vw, 17px);
          line-height: 1.85;
          color: #3D3A34;
          margin-bottom: 18px;
        }
        .post-body code {
          background: #FFF4E0;
          color: #C4811A;
          padding: 2px 7px;
          border-radius: 5px;
          font-size: 0.87em;
          font-family: monospace;
        }
        .post-body strong { color: #1A1814; font-weight: 700; }
        .post-body em     { font-style: italic; color: #3D3A34; }

        @media(max-width: 640px) {
          .post-meta { flex-wrap: wrap; gap: 8px !important; }
        }
      `}</style>

      <PageNav/>
      <main style={{ flex:1 }}>
        {/* Hero */}
        <div style={{ background:`linear-gradient(135deg,#FFF4E0,#FFFDF8)`, borderBottom:`1px solid ${C.border}`, padding:'52px clamp(20px,5vw,60px) 44px' }}>
          <div style={{ maxWidth:760, margin:'0 auto' }}>
            <a href="/blog" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:C.muted, textDecoration:'none', fontFamily:font.body, fontWeight:500, marginBottom:24 }}>
              <ArrowLeft size={14}/> All posts
            </a>
            <div className="post-meta" style={{ display:'flex', alignItems:'center', gap:14, marginBottom:18 }}>
              <span style={{ fontSize:12, fontWeight:700, padding:'3px 11px', borderRadius:20, background:post.color+'18', color:post.color, fontFamily:font.body }}>
                {post.tag}
              </span>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:C.muted, fontFamily:font.body }}>
                <Calendar size={13}/>{post.date}
              </span>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:C.muted, fontFamily:font.body }}>
                <Clock size={13}/>{post.readTime}
              </span>
            </div>
            <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(24px,5vw,40px)', letterSpacing:'-0.04em', color:C.ink, lineHeight:1.12 }}>
              {post.title}
            </h1>
          </div>
        </div>

        {/* Body */}
        <div style={{ maxWidth:760, margin:'0 auto', padding:'48px clamp(20px,5vw,60px) 80px' }}>
          <p style={{ fontSize:'clamp(16px,2vw,18px)', color:C.muted, lineHeight:1.75, fontFamily:font.body, fontStyle:'italic', marginBottom:36, paddingBottom:32, borderBottom:`1px solid ${C.border}` }}>
            {post.excerpt}
          </p>

          <div className="post-body" dangerouslySetInnerHTML={{ __html: post.body }}/>

          {/* CTA */}
          <div style={{ marginTop:56, padding:'28px 32px', borderRadius:20, background:`linear-gradient(135deg,${C.amber100},#fff8eb)`, border:`1px solid ${C.amber200}`, textAlign:'center' }}>
            <p style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(17px,2.5vw,22px)', color:C.ink, marginBottom:8, letterSpacing:'-0.03em' }}>
              Try Jötr free
            </p>
            <p style={{ fontSize:14, color:C.muted, marginBottom:20, fontFamily:font.body }}>
              Full-text search, rich text editor, cloud sync. Free forever.
            </p>
            <a href="/signup" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 28px', borderRadius:11, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, textDecoration:'none', fontFamily:font.body }}>
              Get started →
            </a>
          </div>

          {/* Back */}
          <div style={{ marginTop:40, paddingTop:32, borderTop:`1px solid ${C.border}` }}>
            <a href="/blog" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:14, color:C.muted, textDecoration:'none', fontFamily:font.body, fontWeight:500 }}>
              <ArrowLeft size={14}/> Back to all posts
            </a>
          </div>
        </div>
      </main>
      <PageFooter/>
      <CookieBanner/>
    </>
  );
};

export default BlogPost;